import { useMemo, useState } from 'react';
import type { EffectiveModelAtlas } from '@ayumia/contracts';
import { text } from '../../app/i18n';
import { formatRoute } from '../../app/router';
import { Btn, PageHeader } from '../../design-system/primitives';
import { GraphCanvas, TypeChip, type CanvasCard, type CanvasLane, type CanvasLink } from '../../graph/GraphCanvas';
import { typeVisual } from '../../graph/graph-data';
import { Empty, errorView, Loading } from '../../states/States';
import { useLoad } from '../../views/useLoad';
import { useAppServices } from '../../views/ViewContext';
import { ATLAS_CARD_H, ATLAS_CARD_W, CARDINALITY, layoutAtlas, lineageOfObject, type AtlasObject } from './atlas-layout';
import '../../graph/graph.css';

/**
 * Studio › Modèle (ADR-130 tranche 1, menu Studio ADR-148) : l'atlas du modèle effectif du tenant, tel que le serveur
 * l'utilise (`GetEffectiveModel`), en lecture seule. Objets groupés par la couche qui les apporte, liens et
 * cardinalités de l'objet choisi, panneau de l'objet (chaîne des couches, compteurs, champs, cycle de vie,
 * relations, vues) et « pourquoi ce champ ? » par `ExplainConfiguration`. Même rendu que l'explorateur (ADR-129).
 */

type Atlas = EffectiveModelAtlas;
type Field = AtlasObject['fields'][number];
type Tab = 'fields' | 'lifecycle' | 'relations' | 'views';

const STATES: Readonly<Record<string, string>> = { inherited: 'hérité', added: 'ajouté', extended: 'étendu', overridden: 'surchargé', masked: 'masqué', deprecated: 'déprécié', removed: 'retiré', conflict: 'en conflit' };
const STATE_TONES: Readonly<Record<string, string>> = { inherited: 'neutral', added: 'ok', extended: 'info', overridden: 'warn', masked: 'neutral', deprecated: 'warn', removed: 'danger', conflict: 'danger' };
const TYPES: Readonly<Record<string, string>> = { string: 'texte', number: 'nombre', decimal: 'décimal', boolean: 'oui / non', date: 'date', datetime: 'date et heure', duration: 'durée', money: 'montant', quantity: 'quantité', enumeration: 'liste de valeurs', reference: 'référence', collection: 'collection', file: 'fichier', rich_text: 'texte riche', json: 'structure' };
const GLYPHS: Readonly<Record<string, string>> = { string: 'Aa', rich_text: 'Aa', number: '1,0', decimal: '1,0', money: '€', quantity: '1,0', enumeration: '≡', reference: '→', date: 'JJ', datetime: 'JJ', duration: 'h', boolean: '✓', collection: '[ ]', json: '{ }', file: '⎙' };

export function ModelAtlasPage({ objectType }: { objectType?: string }) {
  const services = useAppServices();
  const [loaded, reload] = useLoad<Atlas>('effective-model', async () => (await services.client.query<Atlas>('GetEffectiveModel')).data);
  return (
    <section className="ay-view ay-graph ay-graph--page" aria-labelledby="ay-model-title">
      <PageHeader titleId="ay-model-title" title="Modèle du tenant" subtitle="Lecture seule" />
      {loaded.status === 'loading' && <Loading label="Lecture du modèle effectif…" />}
      {loaded.status === 'error' && errorView(loaded.error, reload)}
      {loaded.status === 'ready' && <AtlasView atlas={loaded.value} {...(objectType === undefined ? {} : { selected: objectType })} />}
    </section>
  );
}

function layerName(atlas: Atlas, lineage: string | undefined): string {
  if (lineage === undefined) return 'couche inconnue';
  if (lineage === 'core') return 'Core';
  const layer = atlas.layers.find((candidate) => candidate.lineage === lineage);
  if (layer?.label !== undefined) return text(layer.label).replace(/^Pack\s+/, '');
  if (lineage === 'core') return 'Core';
  return lineage.replace(/^(pack|customer|solution):/, '');
}

function AtlasView({ atlas, selected: requested }: { atlas: Atlas; selected?: string }) {
  const services = useAppServices();
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState('');
  const selected = atlas.objects.some((object) => object.object_type === requested) ? requested : (atlas.objects.find((object) => object.relations.length > 0)?.object_type ?? atlas.objects[0]?.object_type);
  const layout = useMemo(() => layoutAtlas(atlas, { hiddenLineages: hidden, ...(selected === undefined ? {} : { selected }) }), [atlas, hidden, selected]);
  const select = (objectType: string): void => services.navigate({ kind: 'model', object_type: objectType });
  const term = search.trim().toLocaleLowerCase('fr');
  const matches = (object: AtlasObject): boolean =>
    term !== '' && [object.object_type, text(object.label), ...object.fields.flatMap((field) => [field.field_id, text(field.label)])].some((value) => value.toLocaleLowerCase('fr').includes(term));
  const neighbours = new Set(layout.links.flatMap((link) => [link.from, link.to]));
  const focus = term !== '' ? new Set(atlas.objects.filter(matches).map((object) => object.object_type)) : neighbours.size > 0 ? neighbours : new Set(selected === undefined ? [] : [selected]);

  const lanes: CanvasLane[] = layout.zones.map((zone) => ({ key: `zone:${zone.lineage}`, x: zone.x, y: 8, w: zone.w, h: layout.height - 16, title: layerName(atlas, zone.lineage), count: `${zone.count} objets` }));
  const cards: CanvasCard[] = layout.cards.map((card) => {
    const visual = typeVisual(card.object.object_type, card.object);
    const isSelected = card.key === selected;
    return {
      key: card.key,
      x: card.x,
      y: card.y,
      w: ATLAS_CARD_W,
      h: ATLAS_CARD_H,
      sides: card.sides,
      tone: visual.tone,
      variant: isSelected ? 'center' : 'node',
      dimmed: !focus.has(card.key) && !isSelected,
      highlighted: term !== '' && focus.has(card.key),
      label: `${text(card.object.label, 'fr', card.object.object_type)}, ${layerName(atlas, card.lineage)}, ${card.object.fields.length} champs${isSelected ? ' (objet choisi)' : ''}`,
      onActivate: () => select(card.key),
      children: (
        <span className="ay-graph__cardbody">
          <TypeChip icon={visual.icon} tone={visual.tone} size={28} />
          <span className="ay-graph__cardtext">
            <strong>{text(card.object.label, 'fr', card.object.object_type)}</strong>
            <span>
              <span className="ay-model__layer">{layerName(atlas, card.lineage)}</span> <span className="ay-graph__muted">{card.object.fields.length} champs</span>
            </span>
          </span>
        </span>
      ),
    };
  });
  const links: CanvasLink[] = layout.links.map((link) => ({
    key: link.key,
    d: link.d,
    width: 2,
    arrow: true,
    dimmed: false,
    highlighted: true,
    selected: false,
    label: { text: `${text(link.relation.label, 'fr', link.relation.relation_id)} · ${CARDINALITY[link.relation.cardinality] ?? link.relation.cardinality}`, x: link.labelX, y: link.labelY },
    ariaLabel: `Relation « ${text(link.relation.label, 'fr', link.relation.relation_id)} » de ${link.relation.source_type} vers ${link.relation.target_type}, ${CARDINALITY[link.relation.cardinality] ?? link.relation.cardinality}`,
    onSelect: () => select(link.from === selected ? link.to : link.from),
  }));
  const lineages = atlas.layers.filter((layer) => atlas.objects.some((object) => lineageOfObject(object) === layer.lineage));
  const object = atlas.objects.find((candidate) => candidate.object_type === selected);

  return (
    <section className="ay-card ay-graph__panel" aria-label="Modèle du tenant">
      <div className="ay-graph__bar">
        <label className="ay-graph__search">
          <span className="ay-visually-hidden">Rechercher un objet ou un champ</span>
          <input type="search" placeholder="Rechercher un objet ou un champ" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <div className="ay-graph__filters ay-model__layers" role="group" aria-label="Couches affichées">
          <span className="ay-graph__muted">Couches :</span>
          {lineages.map((layer) => {
            const shown = !hidden.has(layer.lineage);
            return (
              <button
                key={layer.lineage}
                type="button"
                className="ay-graph__filter"
                aria-pressed={shown}
                onClick={() =>
                  setHidden((current) => {
                    const next = new Set(current);
                    if (shown) next.add(layer.lineage);
                    else next.delete(layer.lineage);
                    return next;
                  })
                }
              >
                {layerName(atlas, layer.lineage)}
              </button>
            );
          })}
        </div>
        <span className="ay-graph__spacer" />
        <span className="ay-graph__muted">{atlas.objects.length} objets</span>
        <span className="ay-graph__mono ay-graph__muted">{atlas.layers.map((layer) => `${layerName(atlas, layer.lineage)} ${layer.version}`).join(' · ')}</span>
      </div>
      <div className="ay-graph__stage">
        {layout.cards.length === 0 ? (
          <Empty label="Aucun objet dans les couches choisies." />
        ) : (
          <GraphCanvas width={layout.width} height={layout.height} lanes={lanes} cards={cards} links={links} label="Atlas des objets du modèle" minimapLabel="Mini-carte du modèle" />
        )}
        {object !== undefined && <ObjectPanel atlas={atlas} object={object} />}
      </div>
    </section>
  );
}

function ObjectPanel({ atlas, object }: { atlas: Atlas; object: AtlasObject }) {
  const services = useAppServices();
  const [tab, setTab] = useState<Tab>('fields');
  const incoming = atlas.objects.flatMap((candidate) => candidate.relations.filter((relation) => relation.target_type === object.object_type && relation.source_type !== object.object_type));
  const relations = [...object.relations, ...incoming];
  const visual = typeVisual(object.object_type, object);
  const chain = object.origin_chain;
  const tabs: ReadonlyArray<{ id: Tab; label: string }> = [
    { id: 'fields', label: 'Champs' },
    { id: 'lifecycle', label: 'Cycle de vie' },
    { id: 'relations', label: 'Relations' },
    { id: 'views', label: 'Vues' },
  ];
  return (
    <aside className="ay-card ay-graph__side ay-model__side" aria-labelledby="ay-model-object">
      <div className="ay-model__head">
        <TypeChip icon={visual.icon} tone={visual.tone} />
        <div>
          <span className="ay-graph__eyebrow">Objet · {layerName(atlas, lineageOfObject(object))}</span>
          <h2 id="ay-model-object">{text(object.label, 'fr', object.object_type)}</h2>
        </div>
      </div>
      <p className="ay-graph__pathline" aria-label="Chaîne des couches">
        {chain.map((step, index) => (
          <span key={`${step.id}@${step.version}`}>
            {index > 0 && <span aria-hidden="true"> › </span>}
            <span className="ay-model__layer">{layerName(atlas, step.lineage)}</span> {step.version}
          </span>
        ))}
      </p>
      <div className="ay-model__counters">
        <div>
          <b>{object.fields.length}</b>
          <span>champs</span>
        </div>
        <div>
          <b>{object.commands.length}</b>
          <span>commandes</span>
        </div>
        <div>
          <b>{object.views.length}</b>
          <span>vues</span>
        </div>
        <div>
          <b>{relations.length}</b>
          <span>relations</span>
        </div>
      </div>
      <div role="tablist" aria-label="Détail de l’objet" className="ay-graph__segmented ay-model__tabs">
        {tabs.map((entry) => (
          <button key={entry.id} type="button" role="tab" id={`ay-model-tab-${entry.id}`} aria-selected={tab === entry.id} aria-pressed={tab === entry.id} aria-controls="ay-model-tabpanel" onClick={() => setTab(entry.id)}>
            {entry.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id="ay-model-tabpanel" aria-labelledby={`ay-model-tab-${tab}`} className="ay-model__tabpanel">
        {tab === 'fields' && (
          <ul className="ay-model__fields">
            {object.fields.map((field) => (
              <FieldRow key={field.field_id} atlas={atlas} object={object} field={field} />
            ))}
          </ul>
        )}
        {tab === 'lifecycle' &&
          (object.lifecycle === undefined ? (
            <Empty label="Cet objet n’a pas de cycle de vie déclaré." />
          ) : (
            <div className="ay-model__lifecycle">
              <p>
                États ({object.lifecycle.state_field}) : {object.lifecycle.states.join(', ')}
                {object.lifecycle.initial_state === undefined ? '' : ` ; initial : ${object.lifecycle.initial_state}`}
                {object.lifecycle.terminal_states === undefined ? '' : ` ; finaux : ${object.lifecycle.terminal_states.join(', ')}`}
              </p>
              <ul>
                {object.lifecycle.transitions.map((transition) => (
                  <li key={`${transition.command}:${transition.to}`}>
                    {transition.from.join(', ')} → <b>{transition.to}</b> <span className="ay-graph__mono">{transition.command}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        {tab === 'relations' &&
          (relations.length === 0 ? (
            <Empty label="Aucune relation déclarée." />
          ) : (
            <ul className="ay-model__fields">
              {relations.map((relation) => (
                <li key={`${relation.source_type}.${relation.relation_id}`}>
                  <div className="ay-model__fieldline">
                    <strong>{text(relation.label, 'fr', relation.relation_id)}</strong>
                    <span className="ay-graph__muted">
                      {relation.source_type} → {relation.target_type} · {CARDINALITY[relation.cardinality] ?? relation.cardinality}
                    </span>
                    {relation.inheritance_state !== undefined && <span className={`ay-model__state ay-model__state--${STATE_TONES[relation.inheritance_state] ?? 'neutral'}`}>{STATES[relation.inheritance_state] ?? relation.inheritance_state}</span>}
                  </div>
                </li>
              ))}
            </ul>
          ))}
        {tab === 'views' &&
          (object.views.length === 0 ? (
            <Empty label="Aucune vue active n’affiche cet objet." />
          ) : (
            <ul className="ay-model__fields">
              {object.views.map((view) => (
                <li key={view.view_ref}>
                  <a
                    href={formatRoute({ kind: 'view', view_ref: view.view_ref })}
                    onClick={(event) => {
                      event.preventDefault();
                      services.navigate({ kind: 'view', view_ref: view.view_ref });
                    }}
                  >
                    {text(view.title, 'fr', view.view_ref)}
                  </a>{' '}
                  <span className="ay-graph__mono ay-graph__muted">{view.version}</span>
                </li>
              ))}
            </ul>
          ))}
      </div>
      <div className="ay-graph__actions">
        <Btn onClick={() => services.navigate({ kind: 'object', object_type: object.object_type })}>Voir les objets réels</Btn>
        <Btn variant="ghost" onClick={() => services.navigate({ kind: 'tenant', tab: 'composition' })}>
          Voir dans la composition
        </Btn>
      </div>
    </aside>
  );
}

type Explanation = { inheritance_state?: string; origin_chain?: Array<{ id: string; version: string }>; changed_by?: string; usages?: Array<{ kind: string; usage: string }> };

/** Une ligne de champ et son « pourquoi ? » (16 §9), lu par `ExplainConfiguration` à la demande. */
function FieldRow({ atlas, object, field }: { atlas: Atlas; object: AtlasObject; field: Field }) {
  const services = useAppServices();
  const [open, setOpen] = useState(false);
  const [explained, setExplained] = useState<{ status: 'loading' } | { status: 'ready'; value: Explanation } | { status: 'error'; message: string } | null>(null);
  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    if (next && explained === null) {
      setExplained({ status: 'loading' });
      services.client.query<Explanation>('ExplainConfiguration', { object_type: object.object_type, field_id: field.field_id }).then(
        (response) => setExplained({ status: 'ready', value: response.data }),
        (error: unknown) => setExplained({ status: 'error', message: error instanceof Error ? error.message : String(error) }),
      );
    }
  };
  const lineageOf = (step: { id: string; version: string }): string | undefined => field.origin_chain.find((entry) => entry.id === step.id && entry.version === step.version)?.lineage;
  return (
    <li className={open ? 'ay-model__field--open' : undefined}>
      <div className="ay-model__fieldline">
        <span className="ay-model__glyph" aria-hidden="true">
          {GLYPHS[field.type] ?? '·'}
        </span>
        <span className="ay-model__fieldname">
          <strong>{text(field.label, 'fr', field.field_id)}</strong>
          <span className="ay-graph__muted">
            {TYPES[field.type] ?? field.type}
            {field.required === true ? ', obligatoire' : ''}
            {field.reference_type === undefined ? '' : ` (${field.reference_type})`}
          </span>
        </span>
        <span className="ay-model__layer">{layerName(atlas, field.lineage ?? field.origin_chain[0]?.lineage)}</span>
        <span className={`ay-model__state ay-model__state--${STATE_TONES[field.inheritance_state] ?? 'neutral'}`}>{STATES[field.inheritance_state] ?? field.inheritance_state}</span>
        <button type="button" className="ay-model__why" aria-expanded={open} aria-label={`Pourquoi le champ ${text(field.label, 'fr', field.field_id)} ?`} title="Pourquoi ce champ ?" onClick={toggle}>
          ?
        </button>
      </div>
      {open && (
        <div className="ay-model__explain" aria-live="polite">
          {explained?.status === 'loading' && <span className="ay-graph__muted">Lecture de l’origine…</span>}
          {explained?.status === 'error' && <span role="alert">{explained.message}</span>}
          {explained?.status === 'ready' && (
            <ol>
              {(explained.value.origin_chain ?? field.origin_chain).map((step, index) => (
                <li key={`${step.id}@${step.version}`}>
                  {index === 0 ? 'Apporté' : 'Touché'} par {layerName(atlas, lineageOf(step))} {step.version} <span className="ay-graph__mono ay-graph__muted">({step.id})</span>
                </li>
              ))}
              <li>
                État : {STATES[explained.value.inheritance_state ?? field.inheritance_state] ?? explained.value.inheritance_state}
                {explained.value.changed_by === undefined ? '' : ` ; dernière couche à l’avoir changé : ${explained.value.changed_by}`}
              </li>
              {explained.value.usages !== undefined && (
                <li>
                  {explained.value.usages.length === 0
                    ? 'Aucun artefact actif ne le cite.'
                    : `Écrit par ${explained.value.usages.filter((usage) => usage.usage === 'writes').length} commande(s), cité par ${explained.value.usages.filter((usage) => usage.usage === 'cites').length} artefact(s) actif(s).`}
                </li>
              )}
            </ol>
          )}
        </div>
      )}
    </li>
  );
}
